import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithPopup, signInWithRedirect, onAuthStateChanged, getRedirectResult } from "firebase/auth";
import { solicitarAccesoUsuario, buscarUsuarioPorCorreo } from '../services/api';
import { initFirebase, googleProvider } from '../firebaseConfig';
import { API_URL } from '../services/api/config';

// Detectar si es móvil
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Light theme color palette
const colors = {
  bg: '#f9fafb',
  surface: '#ffffff',
  surfaceHover: '#f3f4f6',
  border: '#e5e7eb',
  borderFocus: '#84cc16',
  accent: '#84cc16',
  accentHover: '#65a30d',
  textPrimary: '#1f2937',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  error: '#dc2626',
  errorBg: '#fef2f2',
  success: '#16a34a',
  successBg: '#f0fdf4',
  warning: '#d97706',
  warningBg: '#fffbeb',
  info: '#2563eb',
  infoBg: '#eff6ff',
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: colors.bg,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "2rem 1rem",
  },
  card: {
    width: "100%",
    maxWidth: "400px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "12px",
    padding: "2rem",
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    marginBottom: "2rem",
  },
  logoIcon: {
    width: "32px",
    height: "32px",
    backgroundColor: colors.accent,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoText: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: colors.textPrimary,
    letterSpacing: "-0.025em",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: colors.textPrimary,
    textAlign: "center",
    marginBottom: "0.5rem",
    letterSpacing: "-0.025em",
  },
  subtitle: {
    fontSize: "0.875rem",
    color: colors.textSecondary,
    textAlign: "center",
    marginBottom: "1.5rem",
    lineHeight: 1.5,
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.textPrimary,
    marginBottom: "0.5rem",
  },
  input: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    color: colors.textPrimary,
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  inputFocus: {
    borderColor: colors.accent,
    boxShadow: `0 0 0 3px rgba(132, 204, 22, 0.1)`,
  },
  button: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: colors.accent,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s, transform 0.1s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  alert: {
    padding: "0.75rem 1rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
  },
  alertError: {
    backgroundColor: colors.errorBg,
    border: `1px solid ${colors.error}`,
    color: colors.error,
  },
  alertSuccess: {
    backgroundColor: colors.successBg,
    border: `1px solid ${colors.success}`,
    color: colors.success,
  },
  alertWarning: {
    backgroundColor: colors.warningBg,
    border: `1px solid ${colors.warning}`,
    color: colors.warning,
  },
  alertInfo: {
    backgroundColor: colors.infoBg,
    border: `1px solid ${colors.info}`,
    color: colors.info,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "1.5rem 0",
    color: colors.textMuted,
    fontSize: "0.75rem",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: colors.border,
  },
  dividerText: {
    padding: "0 1rem",
  },
  link: {
    color: colors.accent,
    textDecoration: "none",
    fontSize: "0.875rem",
    transition: "opacity 0.2s",
  },
  footer: {
    padding: "1.5rem",
    textAlign: "center",
    borderTop: `1px solid ${colors.border}`,
  },
  footerText: {
    fontSize: "0.75rem",
    color: colors.textMuted,
    marginBottom: "0.5rem",
  },
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: "1.5rem",
  },
  footerLink: {
    color: colors.textSecondary,
    textDecoration: "none",
    fontSize: "0.75rem",
    transition: "color 0.2s",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTopColor: '#ffffff',
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite",
  },
  premiumSection: {
    textAlign: "center",
    padding: "1rem",
    backgroundColor: colors.bg,
    borderRadius: "8px",
    border: `1px solid ${colors.border}`,
  },
  newUserNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75rem",
    padding: "1rem",
    backgroundColor: "#fffbeb",
    borderRadius: "8px",
    border: "1px solid #fcd34d",
    marginBottom: "1rem",
  },
  newUserNoteIcon: {
    fontSize: "1.25rem",
    flexShrink: 0,
  },
  newUserNoteText: {
    fontSize: "0.8rem",
    color: "#92400e",
    lineHeight: 1.5,
    margin: 0,
  },
  premiumTitle: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.textPrimary,
    marginBottom: "0.5rem",
  },
  premiumText: {
    fontSize: "0.75rem",
    color: colors.textSecondary,
    marginBottom: "0.75rem",
    lineHeight: 1.5,
  },
  outlineButton: {
    display: "inline-block",
    padding: "0.5rem 1rem",
    fontSize: "0.75rem",
    fontWeight: 500,
    color: colors.accent,
    backgroundColor: "transparent",
    border: `1px solid ${colors.accent}`,
    borderRadius: "6px",
    textDecoration: "none",
    transition: "background-color 0.2s, color 0.2s",
  },
  googleButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: '#3c4043',
    backgroundColor: '#ffffff',
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s, box-shadow 0.2s",
  },
  googleButtonHover: {
    backgroundColor: '#f8f9fa',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  googleButtonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

// Add keyframes for spinner animation
const spinnerKeyframes = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function UserLogin() {
  const [correo, setCorreo] = useState("");
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [mostrarMensajeSesion, setMostrarMensajeSesion] = useState(false);
  const [intentosRestantes, setIntentosRestantes] = useState(3);
  const [bloqueadoPorRateLimit, setBloqueadoPorRateLimit] = useState(false);
  const [tiempoRestante, setTiempoRestante] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const [googleButtonHovered, setGoogleButtonHovered] = useState(false);
  const navigate = useNavigate();
  const baseUrl = window.location.origin;

  // Procesar login de Google después de autenticación
  const processGoogleLogin = async (user) => {
    const idToken = await user.getIdToken();

    const response = await fetch(`${API_URL}/api/auth/google-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Error al autenticar con Google');
    }

    // Guardar token, método de login y datos del usuario
    localStorage.setItem('nfcToken', data.token);
    localStorage.setItem('loginMethod', 'google');
    localStorage.setItem('userId', data.userId);
    localStorage.setItem('userData', JSON.stringify({
      id: data.userId,
      correoPropietario: user.email
    }));

    // Redirigir al panel de usuario (window.location.replace para limpiar historial OAuth)
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      sessionStorage.removeItem('redirectAfterLogin');
      window.location.replace(redirectPath);
    } else {
      window.location.replace(`/userpanel?doc=${data.userId}&token=${data.token}`);
    }
  };

  // Manejar resultado de Google redirect (para móvil Safari iOS)
  // IMPORTANTE:
  // 1. sessionStorage se pierde en Safari iOS durante redirects OAuth
  // 2. getRedirectResult puede fallar en Safari iOS
  // 3. onAuthStateChanged es el método más confiable
  // 4. NO dependemos de sessionStorage para detectar redirects
  useEffect(() => {
    let unsubscribe = () => {};
    let processed = false;

    const handleRedirectResult = async () => {
      try {
        const { auth } = await initFirebase();

        // PASO 1: Verificar si ya hay sesión activa en nuestra app (no solo Firebase)
        const existingToken = localStorage.getItem('nfcToken');
        const existingUserId = localStorage.getItem('userId');
        const existingLoginMethod = localStorage.getItem('loginMethod');

        // Si ya tenemos sesión válida de nuestra app, no procesar redirect
        if (existingToken && existingUserId && existingLoginMethod === 'google') {
          return;
        }

        // PASO 2: Intentar getRedirectResult PRIMERO
        try {
          const result = await getRedirectResult(auth);
          if (result?.user) {
            processed = true;
            setGoogleLoading(true);
            sessionStorage.removeItem('googleSignInPending');
            await processGoogleLogin(result.user);
            return;
          }
        } catch (redirectErr) {
          // getRedirectResult puede fallar en Safari iOS, continuar con onAuthStateChanged
        }

        // PASO 3: Configurar onAuthStateChanged
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (processed) {
            return;
          }

          if (user) {
            const hasOurSession = localStorage.getItem('nfcToken') && localStorage.getItem('userId');

            if (!hasOurSession) {
              processed = true;
              setGoogleLoading(true);
              sessionStorage.removeItem('googleSignInPending');

              try {
                await processGoogleLogin(user);
              } catch (err) {
                setError(err.message || "Error al iniciar sesión con Google");
                setGoogleLoading(false);
              }
            }
          }
        });

        // PASO 4: Timeout de limpieza
        setTimeout(() => {
          if (!processed) {
            const stillPending = sessionStorage.getItem('googleSignInPending');
            if (stillPending) {
              sessionStorage.removeItem('googleSignInPending');
              setGoogleLoading(false);
            }
          }
        }, 5000);

      } catch (err) {
        sessionStorage.removeItem('googleSignInPending');
        setGoogleLoading(false);
      }
    };

    handleRedirectResult();

    return () => {
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      setMostrarMensajeSesion(true);
    }

    const rateLimitExpiry = localStorage.getItem('rateLimitExpiry');
    if (rateLimitExpiry) {
      const tiempoRestanteMs = parseInt(rateLimitExpiry) - Date.now();
      if (tiempoRestanteMs > 0) {
        setBloqueadoPorRateLimit(true);
        setTiempoRestante(Math.ceil(tiempoRestanteMs / 1000));
      } else {
        localStorage.removeItem('rateLimitExpiry');
      }
    }
  }, []);

  useEffect(() => {
    if (bloqueadoPorRateLimit && tiempoRestante > 0) {
      const timer = setTimeout(() => {
        setTiempoRestante(tiempoRestante - 1);
        if (tiempoRestante - 1 <= 0) {
          setBloqueadoPorRateLimit(false);
          localStorage.removeItem('rateLimitExpiry');
          setIntentosRestantes(3);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [tiempoRestante, bloqueadoPorRateLimit]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setMensaje("");

    if (!correo.trim()) {
      setError("Por favor, escribe tu correo.");
      return;
    }

    if (bloqueadoPorRateLimit) {
      setError(`Has excedido el límite de intentos. Espera ${Math.floor(tiempoRestante / 60)}:${(tiempoRestante % 60).toString().padStart(2, '0')} minutos.`);
      return;
    }

    setLoading(true);

    try {
      const resp = await buscarUsuarioPorCorreo(correo);

      if (resp.status === 404) {
        setError("No encontramos una cuenta con ese correo electrónico.");
        setIntentosRestantes(prev => Math.max(0, prev - 1));
      } else if (resp.status === 429) {
        setBloqueadoPorRateLimit(true);
        const tiempoBloqueo = 15 * 60 * 1000;
        localStorage.setItem('rateLimitExpiry', Date.now() + tiempoBloqueo);
        setTiempoRestante(900);
        setError("Has excedido el límite de intentos. Por favor espera 15 minutos antes de intentar nuevamente.");
      } else if (resp.status === 200 && resp.data) {
        const emailResult = await solicitarAccesoUsuario(correo, baseUrl);

        if (emailResult.error) {
          if (emailResult.error.includes("Demasiados intentos")) {
            setBloqueadoPorRateLimit(true);
            const tiempoBloqueo = 15 * 60 * 1000;
            localStorage.setItem('rateLimitExpiry', Date.now() + tiempoBloqueo);
            setTiempoRestante(900);
            setError("Has excedido el límite de intentos. Por favor espera 15 minutos antes de intentar nuevamente.");
          } else {
            setError(emailResult.error);
            setIntentosRestantes(prev => Math.max(0, prev - 1));
          }
          return;
        }
        setMensaje(
          "¡Listo! En unos momentos recibirás un correo con tu enlace de acceso. El enlace es válido por 2 horas."
        );
        localStorage.setItem('loginMethod', 'magicLink');
        setIntentosRestantes(3);
      } else {
        setError("Ocurrió un error. Por favor intenta nuevamente.");
        setIntentosRestantes(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Error:", err);

      if (err.response && err.response.status === 429) {
        setBloqueadoPorRateLimit(true);
        const tiempoBloqueo = 15 * 60 * 1000;
        localStorage.setItem('rateLimitExpiry', Date.now() + tiempoBloqueo);
        setTiempoRestante(900);
        setError("Has excedido el límite de intentos. Por favor espera 15 minutos antes de intentar nuevamente.");
      } else {
        setError("Error de conexión. Por favor intenta más tarde.");
        setIntentosRestantes(prev => Math.max(0, prev - 1));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError("");
    setMensaje("");
    setGoogleLoading(true);

    try {
      const { auth } = await initFirebase();

      // En móvil usar redirect (evita bloqueo de popup), en desktop usar popup
      if (isMobile) {
        sessionStorage.setItem('googleSignInPending', 'true');
        await signInWithRedirect(auth, googleProvider);
        // El código no continúa aquí - el navegador redirige a Google
        return;
      }

      // Desktop: usar popup (mejor UX)
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Obtener el ID token para verificar en el backend
      const idToken = await user.getIdToken();

      // Llamar al backend para crear/verificar usuario y obtener JWT
      const response = await fetch(`${API_URL}/api/auth/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al autenticar con Google');
      }

      // Guardar token, método de login y datos del usuario (igual que NFC login)
      localStorage.setItem('nfcToken', data.token);
      localStorage.setItem('loginMethod', 'google');
      localStorage.setItem('userId', data.userId);
      localStorage.setItem('userData', JSON.stringify({
        id: data.userId,
        correoPropietario: user.email
      }));

      // Redirigir al panel de usuario (window.location.replace para limpiar historial OAuth)
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        window.location.replace(redirectPath);
      } else {
        window.location.replace(`/userpanel?doc=${data.userId}&token=${data.token}`);
      }

    } catch (err) {
      console.error("Error en Google Sign-In:", err);

      // Manejar errores específicos de Google
      if (err.code === 'auth/popup-closed-by-user') {
        // Usuario cerró el popup, no mostrar error
        return;
      } else if (err.code === 'auth/cancelled-popup-request') {
        // Múltiples popups, ignorar
        return;
      } else if (err.code === 'auth/popup-blocked') {
        setError("El navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para este sitio.");
      } else {
        setError(err.message || "Error al iniciar sesión con Google. Por favor intenta de nuevo.");
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  return (
    <div style={styles.container}>
      <style>{spinnerKeyframes}</style>

      <div style={styles.main}>
        <div style={styles.card}>
          {/* Logo */}
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <span style={styles.logoText}>LemmonTags</span>
          </div>

          {/* Title */}
          <h1 style={styles.title}>Acceder a mi cuenta</h1>
          <p style={styles.subtitle}>
            Ingresa el correo que registraste al activar tu LemmonTag
          </p>

          {/* New user note */}
          <div style={styles.newUserNote}>
            <span style={styles.newUserNoteIcon}>💡</span>
            <p style={styles.newUserNoteText}>
              Para obtener un magic link o acceder con Google necesitas tener una cuenta activa.
            </p>
          </div>

          {/* Session expired message */}
          {mostrarMensajeSesion && (
            <div style={{...styles.alert, ...styles.alertInfo}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
              <span>Tu sesión anterior expiró. Ingresa tu correo para continuar.</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div style={{...styles.alert, ...styles.alertError}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Success message */}
          {mensaje && (
            <div style={{...styles.alert, ...styles.alertSuccess}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>{mensaje}</span>
            </div>
          )}

          {/* Remaining attempts warning */}
          {!bloqueadoPorRateLimit && intentosRestantes < 3 && intentosRestantes > 0 && !mensaje && (
            <div style={{...styles.alert, ...styles.alertWarning}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>Te quedan {intentosRestantes} intento{intentosRestantes !== 1 ? 's' : ''} antes de ser bloqueado temporalmente.</span>
            </div>
          )}

          {/* Rate limit countdown */}
          {bloqueadoPorRateLimit && (
            <div style={{...styles.alert, ...styles.alertError}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>Tiempo restante: {Math.floor(tiempoRestante / 60)}:{(tiempoRestante % 60).toString().padStart(2, '0')}</span>
            </div>
          )}

          {/* Google Sign-In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading || mensaje || bloqueadoPorRateLimit}
            onMouseEnter={() => setGoogleButtonHovered(true)}
            onMouseLeave={() => setGoogleButtonHovered(false)}
            style={{
              ...styles.googleButton,
              ...(googleButtonHovered && !googleLoading && !loading && !mensaje && !bloqueadoPorRateLimit ? styles.googleButtonHover : {}),
              ...((googleLoading || loading || mensaje || bloqueadoPorRateLimit) ? styles.googleButtonDisabled : {}),
            }}
          >
            {googleLoading ? (
              <>
                <div style={{...styles.spinner, borderTopColor: '#3c4043'}} />
                Conectando...
              </>
            ) : (
              <>
                <GoogleIcon />
                Continuar con Google
              </>
            )}
          </button>

          {/* Divider */}
          <div style={styles.divider}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerText}>o ingresa tu correo</span>
            <div style={styles.dividerLine} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1rem" }}>
              <label style={styles.label}>
                Correo electrónico
              </label>
              <input
                type="email"
                placeholder="tu@correo.com"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                disabled={loading || mensaje || bloqueadoPorRateLimit}
                required
                style={{
                  ...styles.input,
                  ...(inputFocused ? styles.inputFocus : {}),
                  opacity: (loading || mensaje || bloqueadoPorRateLimit) ? 0.5 : 1,
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || mensaje || bloqueadoPorRateLimit}
              style={{
                ...styles.button,
                ...((loading || mensaje || bloqueadoPorRateLimit) ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? (
                <>
                  <div style={styles.spinner} />
                  Enviando...
                </>
              ) : bloqueadoPorRateLimit ? (
                "Bloqueado temporalmente"
              ) : (
                "Solicitar acceso"
              )}
            </button>
          </form>

          {/* Resend verification link */}
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <span style={{ fontSize: "0.75rem", color: colors.textSecondary }}>
              ¿No recibiste tu email de activación?{' '}
              <a
                href="/reenviar-verificacion"
                style={styles.link}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/reenviar-verificacion');
                }}
              >
                Reenviar aquí
              </a>
            </span>
          </div>

          {/* Divider before Premium */}
          <div style={styles.divider}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerText}>o</span>
            <div style={styles.dividerLine} />
          </div>

          {/* Premium section */}
          <div style={styles.premiumSection}>
            <h3 style={styles.premiumTitle}>¿Tienes un llavero Premium?</h3>
            <p style={styles.premiumText}>
              Los llaveros Premium con chip NFC te permiten acceder instantáneamente acercándolos a tu teléfono.
            </p>
            <a
              href="https://lemmontags.com/productos"
              style={styles.outlineButton}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver llaveros Premium
            </a>
          </div>
        </div>

        {/* Help link */}
        <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
          <span style={{ fontSize: "0.75rem", color: colors.textMuted }}>
            ¿Problemas para acceder?{' '}
            <a href="mailto:soporte@lemmontags.com" style={styles.link}>
              Contáctanos
            </a>
          </span>
        </div>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>© 2025 LemmonTags</p>
        <div style={styles.footerLinks}>
          <a href="/terminos" style={styles.footerLink}>Términos</a>
          <a href="/privacidad" style={styles.footerLink}>Privacidad</a>
          <a href="https://lemmontags.com" style={styles.footerLink} target="_blank" rel="noopener noreferrer">Tienda</a>
        </div>
      </footer>

    </div>
  );
}
